import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { courseContent } from './courseContent';
import { AuthProvider, useAuth } from './AuthContext';
import { supabase } from './supabaseClient';
import Login from './Login';

// Progress Tracker Component
const ProgressTracker = ({ progress, totalSections, email, onLogout }) => {
  const completedCount = Object.values(progress).filter(Boolean).length;
  const percentage = totalSections > 0 ? Math.round((completedCount / totalSections) * 100) : 0;

  return (
    <div className="progress-tracker">
      <div className="progress-tracker-header">
        <span className="progress-email">{email}</span>
        <button className="logout-btn" onClick={onLogout} title="Sign Out">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
        </button>
      </div>
      <div className="progress-stats-row">
        <span className="progress-label">Progress</span>
        <span className="progress-percentage">{percentage}%</span>
      </div>
      <div className="progress-tracker-bar">
        <div
          className="progress-tracker-fill"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="progress-tracker-stats">
        {completedCount} of {totalSections} sections completed
      </div>
    </div>
  );
};

// Section Completed Checkbox Component
const SectionCompleteCheckbox = ({ isCompleted, onToggle, loading }) => {
  return (
    <div className="section-complete-container">
      <label className={`section-complete-checkbox ${isCompleted ? 'completed' : ''}`}>
        <input
          type="checkbox"
          checked={isCompleted}
          onChange={onToggle}
          disabled={loading}
          style={{ display: 'none' }}
        />
        <span className="checkbox-indicator">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </span>
        <span className="checkbox-label">
          {isCompleted ? 'Section Completed' : 'Mark as Complete'}
        </span>
      </label>
    </div>
  );
};

// Main App Content (separated for auth context)
const AppContent = () => {
  const { user, signOut, loading: authLoading } = useAuth();
  const [currentChapter, setCurrentChapter] = useState(null);
  const [currentSection, setCurrentSection] = useState(null);
  const [expandedChapters, setExpandedChapters] = useState({});
  const [progress, setProgress] = useState({});
  const [progressLoading, setProgressLoading] = useState(false);
  const [quizState, setQuizState] = useState({
    active: false,
    currentQuestion: 0,
    selectedAnswer: null,
    showFeedback: false,
    answers: [],
    shuffledQuestions: []
  });

  // Calculate total sections
  const totalSections = courseContent.chapters.reduce(
    (acc, ch) => acc + ch.sections.length,
    0
  );

  // Load progress from Supabase
  const loadProgress = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .schema('crop').from('user_progress')
        .select('chapter_id, section_index')
        .eq('user_id', user.id);

      if (error) throw error;

      const progressMap = {};
      data?.forEach((item) => {
        progressMap[`${item.chapter_id}-${item.section_index}`] = true;
      });
      setProgress(progressMap);
    } catch (err) {
      console.error('Error loading progress:', err);
    }
  }, [user]);

  // Load progress when user changes
  useEffect(() => {
    if (user) {
      loadProgress();
    } else {
      setProgress({});
    }
  }, [user, loadProgress]);

  // Toggle section completion
  const toggleSectionComplete = async (chapterId, sectionIndex) => {
    if (!user) return;

    const key = `${chapterId}-${sectionIndex}`;
    const isCurrentlyComplete = progress[key];

    setProgressLoading(true);

    try {
      if (isCurrentlyComplete) {
        // Remove completion
        const { error } = await supabase
          .schema('crop').from('user_progress')
          .delete()
          .eq('user_id', user.id)
          .eq('chapter_id', chapterId)
          .eq('section_index', sectionIndex);

        if (error) throw error;

        setProgress((prev) => {
          const updated = { ...prev };
          delete updated[key];
          return updated;
        });
      } else {
        // Add completion
        const { error } = await supabase
          .schema('crop').from('user_progress')
          .insert({
            user_id: user.id,
            chapter_id: chapterId,
            section_index: sectionIndex,
          });

        if (error) throw error;

        setProgress((prev) => ({
          ...prev,
          [key]: true,
        }));
      }
    } catch (err) {
      console.error('Error updating progress:', err);
    } finally {
      setProgressLoading(false);
    }
  };

  // Check if section is completed
  const isSectionCompleted = (chapterId, sectionIndex) => {
    return !!progress[`${chapterId}-${sectionIndex}`];
  };

  // Shuffle array function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Toggle chapter expansion
  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterId]: !prev[chapterId]
    }));
  };

  // Show section content
  const showSection = (chapterId, sectionIndex) => {
    setCurrentChapter(chapterId);
    setCurrentSection(sectionIndex);
    setQuizState(prev => ({ ...prev, active: false }));
    window.scrollTo(0, 0);
  };

  // Start quiz
  const startQuiz = (chapterId) => {
    const chapter = courseContent.chapters.find(c => c.id === chapterId);
    if (!chapter || !chapter.quiz || !chapter.quiz.questions.length) return;

    // Shuffle and take 10 questions
    const shuffled = shuffleArray(chapter.quiz.questions).slice(0, 10);

    setCurrentChapter(chapterId);
    setCurrentSection(null);
    setQuizState({
      active: true,
      currentQuestion: 0,
      selectedAnswer: null,
      showFeedback: false,
      answers: [],
      shuffledQuestions: shuffled
    });
    window.scrollTo(0, 0);
  };

  // Handle answer selection
  const selectAnswer = (index) => {
    if (quizState.showFeedback) return;
    setQuizState(prev => ({ ...prev, selectedAnswer: index }));
  };

  // Submit answer
  const submitAnswer = () => {
    if (quizState.selectedAnswer === null) return;

    const currentQ = quizState.shuffledQuestions[quizState.currentQuestion];
    const isCorrect = quizState.selectedAnswer === currentQ.correctAnswer;

    setQuizState(prev => ({
      ...prev,
      showFeedback: true,
      answers: [...prev.answers, { selected: prev.selectedAnswer, correct: isCorrect }]
    }));
  };

  // Next question
  const nextQuestion = () => {
    if (quizState.currentQuestion < quizState.shuffledQuestions.length - 1) {
      setQuizState(prev => ({
        ...prev,
        currentQuestion: prev.currentQuestion + 1,
        selectedAnswer: null,
        showFeedback: false
      }));
    }
  };

  // Reset quiz
  const resetQuiz = () => {
    startQuiz(currentChapter);
  };

  // Get current chapter
  const getCurrentChapter = () => {
    return courseContent.chapters.find(c => c.id === currentChapter);
  };

  // Calculate quiz score
  const getQuizScore = () => {
    const correct = quizState.answers.filter(a => a.correct).length;
    return {
      correct,
      total: quizState.shuffledQuestions.length,
      percentage: Math.round((correct / quizState.shuffledQuestions.length) * 100)
    };
  };

  // Navigate to next section
  const goToNextSection = () => {
    const chapter = getCurrentChapter();
    if (!chapter) return;

    if (currentSection < chapter.sections.length - 1) {
      showSection(currentChapter, currentSection + 1);
    } else {
      // Go to quiz if available
      if (chapter.quiz && chapter.quiz.questions.length > 0) {
        startQuiz(currentChapter);
      }
    }
  };

  // Navigate to previous section
  const goToPrevSection = () => {
    if (currentSection > 0) {
      showSection(currentChapter, currentSection - 1);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await signOut();
    setCurrentChapter(null);
    setCurrentSection(null);
    setProgress({});
  };

  // Show login screen if not authenticated
  if (authLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  // Render welcome screen
  const renderWelcome = () => (
    <div className="welcome-screen">
      <div className="welcome-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
          <path d="M2 17l10 5 10-5"/>
          <path d="M2 12l10 5 10-5"/>
        </svg>
      </div>
      <h2>{courseContent.title}</h2>
      <p>{courseContent.subtitle}</p>
      <p>This comprehensive course prepares you for the South Dakota Crop Insurance licensing exam. Study at your own pace and test your knowledge with practice quizzes.</p>

      <div className="welcome-stats">
        <div className="stat-item">
          <div className="stat-number">{courseContent.chapters.length}</div>
          <div className="stat-label">Chapters</div>
        </div>
        <div className="stat-item">
          <div className="stat-number">{totalSections}</div>
          <div className="stat-label">Sections</div>
        </div>
        <div className="stat-item">
          <div className="stat-number">{courseContent.estimatedHours}</div>
          <div className="stat-label">Hours</div>
        </div>
      </div>

      <button
        className="start-button"
        onClick={() => {
          setExpandedChapters({ 1: true });
          showSection(1, 0);
        }}
      >
        Start Learning
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      </button>
    </div>
  );

  // Render section content
  const renderSection = () => {
    const chapter = getCurrentChapter();
    if (!chapter || currentSection === null) return null;

    const section = chapter.sections[currentSection];
    if (!section) return null;

    const isCompleted = isSectionCompleted(currentChapter, currentSection);

    return (
      <div className="section-content">
        <div dangerouslySetInnerHTML={{ __html: section.content }} />

        <SectionCompleteCheckbox
          isCompleted={isCompleted}
          onToggle={() => toggleSectionComplete(currentChapter, currentSection)}
          loading={progressLoading}
        />

        <div className="nav-buttons">
          <button
            className="nav-btn"
            onClick={goToPrevSection}
            disabled={currentSection === 0}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Previous
          </button>
          <button
            className="nav-btn"
            onClick={goToNextSection}
          >
            {currentSection === chapter.sections.length - 1 ? 'Take Quiz' : 'Next'}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </button>
        </div>
      </div>
    );
  };

  // Render quiz
  const renderQuiz = () => {
    const chapter = getCurrentChapter();
    if (!chapter || !quizState.active) return null;

    const isComplete = quizState.answers.length === quizState.shuffledQuestions.length &&
                       quizState.currentQuestion === quizState.shuffledQuestions.length - 1 &&
                       quizState.showFeedback;

    if (isComplete) {
      const score = getQuizScore();
      const passed = score.percentage >= 70;

      return (
        <div className="quiz-results">
          <div className="results-icon">{passed ? '🎉' : '📚'}</div>
          <h3 className={`results-title ${passed ? 'passed' : 'failed'}`}>
            {passed ? 'Congratulations!' : 'Keep Studying'}
          </h3>
          <div className={`results-score ${passed ? 'passed' : 'failed'}`}>
            {score.percentage}%
          </div>
          <p className="results-message">
            You got {score.correct} out of {score.total} questions correct.
            {passed
              ? " Great job! You're ready to move on."
              : ' Review the material and try again.'}
          </p>
          <div className="results-buttons">
            <button className="results-btn retry-btn" onClick={resetQuiz}>
              Try Again
            </button>
            {passed && currentChapter < courseContent.chapters.length && (
              <button
                className="results-btn continue-btn"
                onClick={() => {
                  const nextChapterId = currentChapter + 1;
                  setExpandedChapters(prev => ({ ...prev, [nextChapterId]: true }));
                  showSection(nextChapterId, 0);
                }}
              >
                Next Chapter
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </button>
            )}
          </div>
        </div>
      );
    }

    const currentQ = quizState.shuffledQuestions[quizState.currentQuestion];
    if (!currentQ) return null;

    return (
      <div className="quiz-container">
        <div className="quiz-header">
          <h3>{chapter.quiz.title}</h3>
          <div className="quiz-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${((quizState.currentQuestion + 1) / quizState.shuffledQuestions.length) * 100}%` }}
              />
            </div>
            <span className="progress-text">
              {quizState.currentQuestion + 1} / {quizState.shuffledQuestions.length}
            </span>
          </div>
        </div>

        <div className="question-card">
          <div className="question-number">Question {quizState.currentQuestion + 1}</div>
          <div className="question-text">{currentQ.question}</div>

          <div className="options-list">
            {currentQ.options.map((option, index) => {
              let className = 'option-btn';
              if (quizState.selectedAnswer === index) className += ' selected';
              if (quizState.showFeedback) {
                if (index === currentQ.correctAnswer) className += ' correct';
                else if (quizState.selectedAnswer === index) className += ' incorrect';
              }

              return (
                <button
                  key={index}
                  className={className}
                  onClick={() => selectAnswer(index)}
                  disabled={quizState.showFeedback}
                >
                  <span className="option-letter">
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span>{option}</span>
                </button>
              );
            })}
          </div>

          {quizState.showFeedback && (
            <div className={`feedback-box ${quizState.selectedAnswer === currentQ.correctAnswer ? 'correct' : 'incorrect'}`}>
              <div className="feedback-header">
                {quizState.selectedAnswer === currentQ.correctAnswer ? '✓ Correct!' : '✗ Incorrect'}
              </div>
              <div className="feedback-explanation">{currentQ.explanation}</div>
            </div>
          )}

          <div className="quiz-buttons">
            {!quizState.showFeedback ? (
              <button
                className="quiz-btn submit-btn"
                onClick={submitAnswer}
                disabled={quizState.selectedAnswer === null}
              >
                Submit Answer
              </button>
            ) : (
              <button
                className="quiz-btn next-btn"
                onClick={nextQuestion}
                disabled={quizState.currentQuestion === quizState.shuffledQuestions.length - 1}
              >
                {quizState.currentQuestion === quizState.shuffledQuestions.length - 1 ? 'See Results' : 'Next Question'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Count completed sections per chapter
  const getChapterProgress = (chapterId, sectionCount) => {
    let completed = 0;
    for (let i = 0; i < sectionCount; i++) {
      if (progress[`${chapterId}-${i}`]) completed++;
    }
    return completed;
  };

  // Main render
  return (
    <div className="app-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h1>{courseContent.title}</h1>
          <p>{courseContent.subtitle}</p>

          <ProgressTracker
            progress={progress}
            totalSections={totalSections}
            email={user.email}
            onLogout={handleLogout}
          />
        </div>

        <div className="chapter-list">
          {courseContent.chapters.map(chapter => {
            const chapterProgress = getChapterProgress(chapter.id, chapter.sections.length);
            const isChapterComplete = chapterProgress === chapter.sections.length;

            return (
              <div key={chapter.id} className="chapter-item">
                <div
                  className={`chapter-header ${currentChapter === chapter.id ? 'active' : ''}`}
                  onClick={() => toggleChapter(chapter.id)}
                >
                  <span className={`chapter-number ${isChapterComplete ? 'completed' : ''}`}>
                    {isChapterComplete ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    ) : (
                      chapter.id
                    )}
                  </span>
                  <span className="chapter-title">{chapter.title}</span>
                  <span className={`chapter-toggle ${expandedChapters[chapter.id] ? 'expanded' : ''}`}>
                    ▼
                  </span>
                </div>

                {expandedChapters[chapter.id] && (
                  <div className="section-list">
                    {chapter.sections.map((section, idx) => {
                      const sectionComplete = isSectionCompleted(chapter.id, idx);
                      return (
                        <div
                          key={idx}
                          className={`section-item ${currentChapter === chapter.id && currentSection === idx && !quizState.active ? 'active' : ''} ${sectionComplete ? 'completed' : ''}`}
                          onClick={() => showSection(chapter.id, idx)}
                        >
                          {idx + 1}. {section.title}
                        </div>
                      );
                    })}
                    {chapter.quiz && chapter.quiz.questions.length > 0 && (
                      <div
                        className="quiz-link"
                        onClick={() => startQuiz(chapter.id)}
                      >
                        📝 Practice Quiz
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {currentChapter && (
          <div className="content-header">
            <div className="breadcrumb">
              Chapter {currentChapter} {quizState.active ? '› Quiz' : currentSection !== null ? `› Section ${currentSection + 1}` : ''}
            </div>
            <h2>
              {quizState.active
                ? getCurrentChapter()?.quiz?.title
                : getCurrentChapter()?.sections[currentSection]?.title || getCurrentChapter()?.title
              }
            </h2>
          </div>
        )}

        <div className="content-body">
          {!currentChapter && renderWelcome()}
          {currentChapter && !quizState.active && currentSection !== null && renderSection()}
          {currentChapter && quizState.active && renderQuiz()}
        </div>
      </div>
    </div>
  );
};

// Wrap with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
